/**
 * PMV 2.0 Wave 5.5 — Hire Management API client.
 * Preview only — backend returns billable amounts. Never invent FE money authority.
 * Missing rate must never display as 0.
 */
import api from '@/services/api.service';

function unwrap<T>(res: unknown): T {
  const body = (res as { data?: unknown })?.data ?? res;
  return (body as { data?: T })?.data ?? (body as T);
}

export type PmvHireCommercialStatus =
  | 'PLANNED'
  | 'CONFIRMED'
  | 'MOBILIZING'
  | 'ON_HIRE'
  | 'OFF_HIRE_REQUESTED'
  | 'OFF_HIRE_EFFECTIVE'
  | 'DEMOBILIZING'
  | 'RETURNED'
  | 'CLOSED'
  | 'CANCELLED';

export interface PmvHireAgreement {
  id: string;
  agreement_number: string;
  pmv_profile_id: string;
  supplier_id: string;
  purchase_order_id?: string | null;
  purchase_order_line_id?: string | null;
  contract_reference?: string | null;
  currency: string;
  ownership_type: string;
  commercial_status: PmvHireCommercialStatus | string;
  planned_on_hire_date?: string | null;
  planned_off_hire_date?: string | null;
  commercial_on_hire_at?: string | null;
  commercial_off_hire_requested_at?: string | null;
  commercial_off_hire_effective_at?: string | null;
  physical_mobilize_at?: string | null;
  physical_demobilize_at?: string | null;
  project_id?: string | null;
  site_reference?: string | null;
  work_front_id?: string | null;
  operator_responsibility?: string | null;
  fuel_responsibility?: string | null;
  maintenance_responsibility?: string | null;
  tyre_responsibility?: string | null;
  mob_responsibility?: string | null;
  demob_responsibility?: string | null;
  tax_treatment_ref?: string | null;
  notes?: string | null;
  idempotent_replay?: boolean;
}

export interface PmvHireRate {
  id: string;
  hire_agreement_id: string;
  rate_code: string;
  label: string;
  rate_basis: string;
  hourly_rate?: string | number | null;
  daily_rate?: string | number | null;
  monthly_rate?: string | number | null;
  currency: string;
  daily_minimum_hours?: string | number | null;
  standby_pct?: string | number | null;
  breakdown_pct?: string | number | null;
  idle_pct?: string | number | null;
  overtime_multiplier?: string | number | null;
  monthly_proration_days?: number | null;
  effective_from: string;
  effective_until?: string | null;
  is_active?: boolean;
  notes?: string | null;
}

export interface PmvHireEffectiveRate {
  rate: PmvHireRate | null;
  available: boolean;
  as_of: string;
  message?: string;
}

export interface PmvHireControlTower {
  wave?: string;
  by_commercial_status: Record<string, number>;
  on_hire_count: number;
  open_attention: number;
  stale_calculations: number;
  open_reconciliations: number;
  /** Optional extended KPIs when backend provides them — never coerce null→0 for money. */
  hire_cost_mtd_estimated?: number | string | null;
  hire_cost_mtd_accrued?: number | string | null;
  hire_cost_mtd_final?: number | string | null;
  hire_cost_mtd_complete?: boolean;
  unverified_logs?: number | null;
  timesheets_pending?: number | null;
  invoice_variance_count?: number | null;
  off_hire_pending?: number | null;
  rules?: Record<string, boolean>;
  note?: string;
}

export interface PmvHireListResult<T> {
  items: T[];
  total: number;
}

export interface PmvHireCalculation {
  id: string;
  calculation_number?: string;
  hire_agreement_id: string;
  period_from: string;
  period_to: string;
  status: string;
  total_amount?: string | number | null;
  currency?: string;
  rate_available?: boolean;
  explanation_json?: Record<string, unknown> | null;
  lines?: Array<Record<string, unknown>>;
}

export interface PmvHireTimesheet {
  id: string;
  timesheet_number?: string;
  hire_agreement_id: string;
  calculation_id?: string;
  status: string;
  period_from?: string;
  period_to?: string;
  total_amount?: string | number | null;
  currency?: string;
  notes?: string | null;
  lines?: Array<Record<string, unknown>>;
  calculation?: PmvHireCalculation;
}

export interface PmvHireStatement {
  id: string;
  statement_number?: string;
  hire_agreement_id: string;
  timesheet_id: string;
  status: string;
  total_amount?: string | number | null;
  currency?: string;
  lines?: Array<Record<string, unknown>>;
}

export interface PmvHireReconciliation {
  id: string;
  recon_number?: string;
  hire_agreement_id: string;
  statement_id: string;
  supplier_invoice_id?: string | null;
  purchase_order_id?: string | null;
  expected_amount?: string | number | null;
  invoiced_amount?: string | number | null;
  variance_amount?: string | number | null;
  variance_type?: string;
  variance_note?: string;
  status: string;
  ap_handoff_status?: string | null;
  lines?: Array<Record<string, unknown>>;
}

export interface PmvHireAttention {
  id: string;
  attention_code: string;
  title: string;
  message?: string | null;
  severity: string;
  status: string;
  hire_agreement_id?: string | null;
  pmv_profile_id?: string | null;
  evidence_json?: Record<string, unknown> | null;
  created_at?: string;
  last_seen_at?: string;
}

/** Format money from backend only. Null/undefined → Unavailable (never 0). */
export function formatHireMoney(
  value: number | string | null | undefined,
  currency?: string
): string {
  if (value == null || value === '') return 'Unavailable';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 'Unavailable';
  const formatted = n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${formatted} ${currency}` : formatted;
}

const pmvHireService = {
  async getMeta() {
    const res = await api.get('/pmv/hire/meta');
    return unwrap<Record<string, unknown>>(res);
  },

  async getControlTower() {
    const res = await api.get('/pmv/hire/control-tower');
    return unwrap<PmvHireControlTower>(res);
  },

  async getDiagnostics() {
    const res = await api.get('/pmv/hire/diagnostics');
    return unwrap<Record<string, unknown>>(res);
  },

  async listAgreements(params?: {
    status?: string;
    pmv_profile_id?: string;
    supplier_id?: string;
    project_id?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }) {
    const res = await api.get('/pmv/hire/agreements', { params });
    return unwrap<PmvHireListResult<PmvHireAgreement>>(res);
  },

  async createAgreement(body: Record<string, unknown>) {
    const res = await api.post('/pmv/hire/agreements', body);
    return unwrap<PmvHireAgreement>(res);
  },

  async getAgreement(id: string) {
    const res = await api.get(`/pmv/hire/agreements/${id}`);
    return unwrap<PmvHireAgreement>(res);
  },

  async patchAgreement(id: string, body: Record<string, unknown>) {
    const res = await api.patch(`/pmv/hire/agreements/${id}`, body);
    return unwrap<PmvHireAgreement>(res);
  },

  async onHire(id: string, body?: Record<string, unknown>) {
    const res = await api.post(`/pmv/hire/agreements/${id}/on-hire`, body ?? {});
    return unwrap<PmvHireAgreement>(res);
  },

  async offHireRequest(id: string, body?: Record<string, unknown>) {
    const res = await api.post(`/pmv/hire/agreements/${id}/off-hire-request`, body ?? {});
    return unwrap<PmvHireAgreement>(res);
  },

  async offHireEffective(id: string, body?: Record<string, unknown>) {
    const res = await api.post(`/pmv/hire/agreements/${id}/off-hire-effective`, body ?? {});
    return unwrap<PmvHireAgreement>(res);
  },

  async demobilize(id: string, body?: Record<string, unknown>) {
    const res = await api.post(`/pmv/hire/agreements/${id}/demobilize`, body ?? {});
    return unwrap<PmvHireAgreement>(res);
  },

  async listRates(agreementId: string, activeOnly = true) {
    const res = await api.get(`/pmv/hire/agreements/${agreementId}/rates`, {
      params: { active_only: activeOnly ? '1' : '0' },
    });
    return unwrap<PmvHireRate[] | { items: PmvHireRate[] }>(res);
  },

  async createRate(agreementId: string, body: Record<string, unknown>) {
    const res = await api.post(`/pmv/hire/agreements/${agreementId}/rates`, body);
    return unwrap<PmvHireRate>(res);
  },

  async resolveEffectiveRate(agreementId: string, asOf?: string) {
    const res = await api.get(`/pmv/hire/agreements/${agreementId}/rates/effective`, {
      params: asOf ? { as_of: asOf } : undefined,
    });
    return unwrap<PmvHireEffectiveRate | PmvHireRate | null>(res);
  },

  async listCalculations(params?: {
    hire_agreement_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const res = await api.get('/pmv/hire/calculations', { params });
    return unwrap<PmvHireListResult<PmvHireCalculation>>(res);
  },

  async computeCalculation(body: {
    hire_agreement_id: string;
    period_from: string;
    period_to: string;
    idempotency_key?: string | null;
  }) {
    const res = await api.post('/pmv/hire/calculations', body);
    return unwrap<PmvHireCalculation>(res);
  },

  async getCalculation(id: string) {
    const res = await api.get(`/pmv/hire/calculations/${id}`);
    return unwrap<PmvHireCalculation>(res);
  },

  async listTimesheets(params?: {
    hire_agreement_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const res = await api.get('/pmv/hire/timesheets', { params });
    return unwrap<PmvHireListResult<PmvHireTimesheet>>(res);
  },

  async createTimesheet(body: {
    calculation_id: string;
    notes?: string | null;
    idempotency_key?: string | null;
  }) {
    const res = await api.post('/pmv/hire/timesheets', body);
    return unwrap<PmvHireTimesheet>(res);
  },

  async getTimesheet(id: string) {
    const res = await api.get(`/pmv/hire/timesheets/${id}`);
    return unwrap<PmvHireTimesheet>(res);
  },

  async submitTimesheet(id: string) {
    const res = await api.post(`/pmv/hire/timesheets/${id}/submit`);
    return unwrap<PmvHireTimesheet>(res);
  },

  async reviewTimesheet(id: string, body?: Record<string, unknown>) {
    const res = await api.post(`/pmv/hire/timesheets/${id}/review`, body ?? {});
    return unwrap<PmvHireTimesheet>(res);
  },

  async certifyTimesheet(id: string) {
    const res = await api.post(`/pmv/hire/timesheets/${id}/certify`);
    return unwrap<PmvHireTimesheet>(res);
  },

  async listStatements(params?: {
    hire_agreement_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const res = await api.get('/pmv/hire/statements', { params });
    return unwrap<PmvHireListResult<PmvHireStatement>>(res);
  },

  async createStatement(body: {
    timesheet_id: string;
    notes?: string | null;
    idempotency_key?: string | null;
  }) {
    const res = await api.post('/pmv/hire/statements', body);
    return unwrap<PmvHireStatement>(res);
  },

  async getStatement(id: string) {
    const res = await api.get(`/pmv/hire/statements/${id}`);
    return unwrap<PmvHireStatement>(res);
  },

  async patchStatement(id: string, body: Record<string, unknown>) {
    const res = await api.patch(`/pmv/hire/statements/${id}`, body);
    return unwrap<PmvHireStatement>(res);
  },

  async certifyStatement(id: string) {
    const res = await api.post(`/pmv/hire/statements/${id}/certify`);
    return unwrap<PmvHireStatement>(res);
  },

  async listReconciliations(params?: {
    hire_agreement_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const res = await api.get('/pmv/hire/reconciliations', { params });
    return unwrap<PmvHireListResult<PmvHireReconciliation>>(res);
  },

  async createReconciliation(body: Record<string, unknown>) {
    const res = await api.post('/pmv/hire/reconciliations', body);
    return unwrap<PmvHireReconciliation>(res);
  },

  async getReconciliation(id: string) {
    const res = await api.get(`/pmv/hire/reconciliations/${id}`);
    return unwrap<PmvHireReconciliation>(res);
  },

  async resolveReconciliation(id: string, body?: Record<string, unknown>) {
    const res = await api.post(`/pmv/hire/reconciliations/${id}/resolve`, body ?? {});
    return unwrap<PmvHireReconciliation>(res);
  },

  async listAttention(params?: {
    status?: string;
    hire_agreement_id?: string;
    severity?: string;
    limit?: number;
    offset?: number;
  }) {
    const res = await api.get('/pmv/hire/attention', { params });
    return unwrap<PmvHireListResult<PmvHireAttention>>(res);
  },

  async upsertAttention(body: Record<string, unknown>) {
    const res = await api.post('/pmv/hire/attention', body);
    return unwrap<PmvHireAttention>(res);
  },

  async ackAttention(id: string) {
    const res = await api.post(`/pmv/hire/attention/${id}/ack`);
    return unwrap<PmvHireAttention>(res);
  },

  async dismissAttention(id: string) {
    const res = await api.post(`/pmv/hire/attention/${id}/dismiss`);
    return unwrap<PmvHireAttention>(res);
  },
};

export default pmvHireService;
